import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  CircularProgress,
  Alert,
  IconButton,
  Switch,
  FormControlLabel,
  MenuItem
} from '@mui/material';

import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

import axiosInstance from '../../api/axiosConfig';



const Products = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [openDialog, setOpenDialog] = useState(false);
  const [openCategoryDialog, setOpenCategoryDialog] = useState(false);

  const [categories, setCategories] = useState([]);

  const [categoryForm, setCategoryForm] = useState({
    categoryName: '',
    isActive: true
  });

  const [editingProduct, setEditingProduct] =
    useState(null);

  const [uploading, setUploading] =
    useState(false);

  const [formData, setFormData] = useState({
    productName: '',
    categoryId: '',
    brand: '',
    price: '',
    shortDescription: '',
    description: '',
    imageUrl: '',
    whatsAppNumber: '',
    displayOrder: 0,
    isActive: true
  });

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, []);

  const fetchProducts = async () => {
    try {
      setLoading(true);

      const response =
        await axiosInstance.get('/product');

      setProducts(
        Array.isArray(response.data)
          ? response.data
          : response.data.data || []
      );

      setError(null);
    } catch (err) {
      console.error(err);
      setError('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response =
        await axiosInstance.get(
          '/product/categories'
        );

      setCategories(response.data);
    }
    catch (err) {
      console.error(err);
    }
  };

  const handleOpenDialog = async (
    product = null
  ) => {
    if (product) {
      try {
        const res =
          await axiosInstance.get(
            `/product/${product.productId}`
          );

        const data = res.data;

        setEditingProduct(data);

        setFormData({
  productName: data.productName || '',
  categoryId: data.categoryId || '',
  brand: data.brand || '',
  price: data.price || '',
  shortDescription: data.shortDescription || '',
  description: data.description || '',
  imageUrl: data.imageUrl || '',
  whatsAppNumber: data.whatsAppNumber || '',
  displayOrder: data.displayOrder || 0,
  isActive: data.isActive ?? true
});
      } catch {
        alert(
          'Failed to load product'
        );
        return;
      }
    } else {
      setEditingProduct(null);

      setFormData({
  productName: '',
  categoryId: '',
  brand: '',
  price: '',
  shortDescription: '',
  description: '',
  imageUrl: '',
  whatsAppNumber: '',
  displayOrder: 0,
  isActive: true
});
    }

    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingProduct(null);
  };

  const handleFormChange = (e) => {
    const {
      name,
      value,
      type,
      checked
    } = e.target;

    setFormData(prev => ({
      ...prev,
      [name]:
        type === 'checkbox'
          ? checked
          : value
    }));
  };

  const handleImageUpload = async (
    event
  ) => {
    const file =
      event.target.files?.[0];

    if (!file) return;

    try {
      setUploading(true);

      const uploadData =
        new FormData();

      uploadData.append(
        'file',
        file
      );

      const response =
        await axiosInstance.post(
          '/product/upload',
          uploadData,
          {
            headers: {
              'Content-Type':
                'multipart/form-data'
            }
          }
        );

      setFormData(prev => ({
        ...prev,
        imageUrl:
          response.data.imageUrl
      }));
    } catch {
      alert(
        'Failed to upload image'
      );
    } finally {
      setUploading(false);
    }
  };

  const handleSaveProduct =
    async () => {
      try {
        if (editingProduct) {
          await axiosInstance.put(
            `/product/${editingProduct.productId}`,
            formData
          );
        } else {
          await axiosInstance.post(
            '/product',
            formData
          );
        }

        handleCloseDialog();

        fetchProducts();
      } catch {
        alert(
          'Error saving product'
        );
      }
    };
  const handleSaveCategory =
    async () => {

      try {

        await axiosInstance.post(
          '/product/categories',
          categoryForm
        );

        setCategoryForm({
          categoryName: '',
          isActive: true
        });

        setOpenCategoryDialog(false);

        fetchCategories();

      } catch (err) {

        alert(
          'Error saving category'
        );

      }
    };

    const handleDeleteCategory =
  async (id) => {

    if (
      !window.confirm(
        'Delete this category?'
      )
    )
      return;

    try {

      await axiosInstance.delete(
        `/product/categories/${id}`
      );

      fetchCategories();

    } catch (err) {

      alert(
        err?.response?.data ||
        'Unable to delete category'
      );

    }
  };

  const handleDeleteProduct =
    async (id) => {
      if (
        !window.confirm(
          'Delete this product?'
        )
      )
        return;

      try {
        await axiosInstance.delete(
          `/product/${id}`
        );

        fetchProducts();
      } catch {
        alert(
          'Error deleting product'
        );
      }
    };
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          mt: 10
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>

      <Box
        sx={{
          mb: 4,
          display: 'flex',
          justifyContent: 'space-between'
        }}
      >
        <Typography
          variant="h4"
          fontWeight="bold"
        >
          Product Management
        </Typography>

        <Box sx={{
          display: 'flex',
          gap: 2
        }}>
          <Button
            variant="outlined"
            onClick={() =>
              setOpenCategoryDialog(true)
            }
          >
            Add Category
          </Button>

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() =>
              handleOpenDialog()
            }
          >
            Add Product
          </Button>
        </Box>

      </Box>

      {error && (
        <Alert severity="error">
          {error}
        </Alert>
      )}

      <TableContainer
        component={Paper}
      >
        <Table>
          <TableHead
            sx={{
              backgroundColor:
                '#1976d2'
            }}
          >
            <TableRow>
              <TableCell
                sx={{
                  color: '#fff'
                }}
              >
                Image
              </TableCell>

              <TableCell
                sx={{
                  color: '#fff'
                }}
              >
                Product
              </TableCell>

              <TableCell
                sx={{
                  color: '#fff'
                }}
              >
                Category
              </TableCell>

              <TableCell
                sx={{
                  color: '#fff'
                }}
              >
                Price
              </TableCell>

              <TableCell
                sx={{
                  color: '#fff'
                }}
              >
                Status
              </TableCell>

              <TableCell
                align="right"
                sx={{
                  color: '#fff'
                }}
              >
                Actions
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>

            {Array.isArray(products) &&
              products.map(product => (

                <TableRow
                  key={product.productId}
                >

                  <TableCell>

                    {product.imageUrl ? (
                      <img
                        src={
                          product.imageUrl
                        }
                        alt=""
                        width="60"
                        height="60"
                        style={{
                          objectFit:
                            'cover',
                          borderRadius:
                            '6px'
                        }}
                      />
                    ) : (
                      '-'
                    )}

                  </TableCell>

                  <TableCell>
                    {product.productName}
                  </TableCell>

                  <TableCell>
  {product.category?.categoryName || ''}
</TableCell>

                  <TableCell>
                    ₹ {product.price}
                  </TableCell>

                  <TableCell>

                    {product.isActive
                      ? 'Active'
                      : 'Inactive'}

                  </TableCell>

                  <TableCell
                    align="right"
                  >

                    <IconButton
                      color="primary"
                      onClick={() =>
                        handleOpenDialog(
                          product
                        )
                      }
                    >
                      <EditIcon />
                    </IconButton>

                    <IconButton
                      color="error"
                      onClick={() =>
                        handleDeleteProduct(
                          product.productId
                        )
                      }
                    >
                      <DeleteIcon />
                    </IconButton>

                  </TableCell>

                </TableRow>

              ))}

          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog */}

      <Dialog
        open={openDialog}
        onClose={
          handleCloseDialog
        }
        maxWidth="md"
        fullWidth
      >

        <DialogTitle>

          {editingProduct
            ? 'Edit Product'
            : 'Add Product'}

        </DialogTitle>

        <DialogContent>

          <Grid
            container
            spacing={2}
            sx={{ mt: 1 }}
          >

            <Grid
              item
              xs={12}
              md={6}
            >
              <TextField
                fullWidth
                label="Product Name"
                name="productName"
                value={
                  formData.productName
                }
                onChange={
                  handleFormChange
                }
              />
            </Grid>

            <Grid
              item
              xs={12}
              md={6}
            >
              <TextField
  select
  fullWidth
  label="Category"
  name="categoryId"
  value={formData.categoryId || ''}
  onChange={handleFormChange}
>
  {categories.map(category => (
    <MenuItem
      key={category.categoryId}
      value={category.categoryId}
    >
      {category.categoryName}
    </MenuItem>
  ))}
</TextField>
            </Grid>

            <Grid
              item
              xs={12}
              md={6}
            >
              <TextField
                fullWidth
                label="Brand"
                name="brand"
                value={
                  formData.brand
                }
                onChange={
                  handleFormChange
                }
              />
            </Grid>

            <Grid
              item
              xs={12}
              md={6}
            >
              <TextField
                fullWidth
                label="Price"
                type="number"
                name="price"
                value={
                  formData.price
                }
                onChange={
                  handleFormChange
                }
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Short Description"
                name="shortDescription"
                value={
                  formData.shortDescription
                }
                onChange={
                  handleFormChange
                }
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Description"
                name="description"
                value={
                  formData.description
                }
                onChange={
                  handleFormChange
                }
              />
            </Grid>

            <Grid
              item
              xs={12}
              md={6}
            >
              <TextField
                fullWidth
                label="WhatsApp Number"
                name="whatsAppNumber"
                value={
                  formData.whatsAppNumber
                }
                onChange={
                  handleFormChange
                }
              />
            </Grid>

            <Grid
              item
              xs={12}
              md={6}
            >
              <TextField
                fullWidth
                label="Display Order"
                type="number"
                name="displayOrder"
                value={
                  formData.displayOrder
                }
                onChange={
                  handleFormChange
                }
              />
            </Grid>

            <Grid item xs={12}>

              <Button
                variant="outlined"
                component="label"
              >
                Upload Image

                <input
                  hidden
                  type="file"
                  accept="image/*"
                  onChange={
                    handleImageUpload
                  }
                />

              </Button>

              {uploading && (
                <CircularProgress
                  size={20}
                  sx={{ ml: 2 }}
                />
              )}

            </Grid>

            {formData.imageUrl && (

              <Grid item xs={12}>

                <img
                  src={
                    formData.imageUrl
                  }
                  alt=""
                  style={{
                    width: 150,
                    borderRadius: 8
                  }}
                />

              </Grid>

            )}

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={
                      formData.isActive
                    }
                    onChange={
                      handleFormChange
                    }
                    name="isActive"
                  />
                }
                label="Active Product"
              />
            </Grid>

          </Grid>

        </DialogContent>

        <DialogActions>

          <Button
            onClick={
              handleCloseDialog
            }
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={
              handleSaveProduct
            }
          >
            {editingProduct
              ? 'Update'
              : 'Save'}
          </Button>

        </DialogActions>

      </Dialog>

      <Dialog
        open={openCategoryDialog}
        onClose={() =>
          setOpenCategoryDialog(false)
        }
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Add Category
        </DialogTitle>

        <DialogContent>

          <TextField
            fullWidth
            label="Category Name"
            sx={{ mt: 2 }}
            value={
              categoryForm.categoryName
            }
            onChange={(e) =>
              setCategoryForm({
                ...categoryForm,
                categoryName:
                  e.target.value
              })
            }
          />

          <Box sx={{ mt: 3 }}>

  <Typography
    variant="subtitle1"
    fontWeight="bold"
    gutterBottom
  >
    Existing Categories
  </Typography>

  {categories.map(category => (

    <Box
      key={category.categoryId}
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        p: 1,
        borderBottom: '1px solid #eee'
      }}
    >

      <Typography>
        {category.categoryName}
      </Typography>

      <Button
        color="error"
        size="small"
        onClick={() =>
          handleDeleteCategory(
            category.categoryId
          )
        }
      >
        Delete
      </Button>

    </Box>

  ))}

</Box>

        </DialogContent>

        <DialogActions>

          <Button
            onClick={() =>
              setOpenCategoryDialog(false)
            }
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={handleSaveCategory}
          >
            Save
          </Button>

        </DialogActions>

      </Dialog>

    </Box>
  );
};

export default Products;